import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, PenLine, Mic } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import TaskForm from '../components/TaskForm';
import ListeningOverlay from '../components/ListeningOverlay';
import VoiceButton from '../components/VoiceButton';
import { motion, AnimatePresence } from 'framer-motion';

const CreateTask: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { tasks } = useApp();
  const [inputMethod, setInputMethod] = useState<'choice' | 'manual' | 'ai'>('choice');
  
  const task = id ? tasks.find(t => t.id === id) : undefined;
  const isEditing = Boolean(task);

  const renderContent = () => {
    if (isEditing) {
      return (
        <Card className="bg-white/50 backdrop-blur-lg">
          <CardContent className="p-6">
            <TaskForm task={task} onSuccess={() => navigate(-1)} />
          </CardContent>
        </Card>
      );
    }

    switch (inputMethod) {
      case 'choice':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card 
              className="bg-white/50 backdrop-blur-lg hover:bg-white/60 transition-all cursor-pointer group"
              onClick={() => setInputMethod('manual')}
            >
              <CardContent className="p-6 flex flex-col items-center justify-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <PenLine className="w-8 h-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Manual Input</h3>
                  <p className="text-sm text-gray-500">Create a task by filling out a form with all the details</p>
                </div>
              </CardContent>
            </Card>

            <Card 
              className="bg-white/50 backdrop-blur-lg hover:bg-white/60 transition-all cursor-pointer group"
              onClick={() => setInputMethod('ai')}
            >
              <CardContent className="p-6 flex flex-col items-center justify-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Mic className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">AI Voice Input</h3>
                  <p className="text-sm text-gray-500">Just speak naturally and let AI create your task</p>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'manual':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="bg-white/50 backdrop-blur-lg">
              <CardContent className="p-6">
                <TaskForm onSuccess={() => navigate(-1)} />
              </CardContent>
            </Card>
          </motion.div>
        );

      case 'ai':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex flex-col items-center justify-center py-12"
          >
            <VoiceButton className="h-28 w-28 bg-gradient-to-r from-blue-500/80 to-purple-500/80 hover:from-blue-500/90 hover:to-purple-500/90" />
            <p className="mt-6 text-gray-600 text-center">
              Tap the microphone and say something like<br />
              "Remind me to call Mom tomorrow"
            </p>
          </motion.div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50 p-4 md:p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => inputMethod === 'choice' ? navigate(-1) : setInputMethod('choice')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                {isEditing ? 'Edit Task' : 'Create Task'}
              </h1>
              <p className="text-gray-500 mt-1">
                {isEditing ? 'Update your task details' : 
                 inputMethod === 'choice' ? 'Choose how to create your task' :
                 inputMethod === 'manual' ? 'Fill in the task details' :
                 'Speak your task naturally'}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {renderContent()}
        </AnimatePresence>
      </div>
      
      <ListeningOverlay />
    </div>
  );
};

export default CreateTask; 